import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { useToast } from '../store/ToastContext';
import { subscribeToTab, updateTab, deleteTab } from '../services/tabs';
import { getMultipleUserProfiles } from '../services/users';
import type { Tab, TabItem, UserProfile, ManualMember } from '../types';
import { GuestService } from '../services/guest';
import { MAX_TAB_ITEMS, MAX_TAB_MEMBERS } from '../constants';
import { calculateUserTotal, calculateBillTotal } from '../utils/tabCalculations';
import type { ScannedReceiptResult } from '../services/receiptScanner';

export const useTabDetails = (tabId: string | undefined) => {
    const navigate = useNavigate();
    const { user } = useAuth(); // Global Auth (Firebase)
    const { showToast } = useToast();

    const [tab, setTab] = useState<Tab | null>(null);
    const [memberProfiles, setMemberProfiles] = useState<UserProfile[]>([]);
    const [loading, setLoading] = useState(true);

    // Identity State
    const [localGuestId, setLocalGuestId] = useState<string | null>(() => {
        if (tabId) return localStorage.getItem(`tab_guest_identity_${tabId}`);
        return null;
    });

    // Check Local Storage for Guest ID
    useEffect(() => {
        if (tabId) {
            // If No User (Guest), add to history
            if (!user) {
                GuestService.saveGuestTab(tabId);
            }
        }
    }, [tabId, user]);

    // Modal States
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isAddItemOpen, setIsAddItemOpen] = useState(false);
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
    const [isItemAssignmentModalOpen, setIsItemAssignmentModalOpen] = useState(false);
    const [viewingItem, setViewingItem] = useState<TabItem | null>(null);

    // Confirm dialog states
    const [isRemoveMemberDialogOpen, setIsRemoveMemberDialogOpen] = useState(false);
    const [memberToRemove, setMemberToRemove] = useState<string | null>(null);
    const [isDeleteItemDialogOpen, setIsDeleteItemDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<string | null>(null);
    const [isDeleteTabDialogOpen, setIsDeleteTabDialogOpen] = useState(false);
    const [isAddManualUserOpen, setIsAddManualUserOpen] = useState(false);
    const [isRemoveManualUserDialogOpen, setIsRemoveManualUserDialogOpen] = useState(false);
    const [manualUserToRemove, setManualUserToRemove] = useState<string | null>(null);

    // Ownership Transfer states
    const [isTransferOwnershipOpen, setIsTransferOwnershipOpen] = useState(false);
    const [isTransferConfirmOpen, setIsTransferConfirmOpen] = useState(false);

    // Claim Quantity state
    const [isClaimQuantityOpen, setIsClaimQuantityOpen] = useState(false);
    const [claimQuantityItem, setClaimQuantityItem] = useState<TabItem | null>(null);

    // Determine effective user ID (Auth UID or Local Guest ID)
    const effectiveUserId = user?.uid ?? localGuestId;
    const isOwner = Boolean(user?.uid && tab?.ownerId === user.uid); // Only authenticated users can be owners

    // Derived State: Membership
    const isMember = useMemo(() => {
        if (!tab) return false;
        // 1. Check Authenticated Member
        if (user && tab.members.includes(user.uid)) return true;
        // 2. Check Guest Member (Manual)
        if (localGuestId && tab.manualMembers?.some(m => m.id === localGuestId)) return true;
        return false;
    }, [tab, user, localGuestId]);

    // ── Subscriptions ──────────────────────────────────────────────────────────

    useEffect(() => {
        if (tabId) {
            const unsubscribe = subscribeToTab(tabId, (updatedTab) => {
                if (updatedTab) {
                    setTab(updatedTab);
                }
                setLoading(false);
            });
            return () => { unsubscribe(); };
        }
    }, [tabId]);

    // Fetch Profiles for Registered Members
    useEffect(() => {
        if (tab?.members && tab.members.length > 0) {
            void getMultipleUserProfiles(tab.members).then(setMemberProfiles);
        } else if (memberProfiles.length > 0) {
            queueMicrotask(() => setMemberProfiles([])); // Clear if no members
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab?.members]);

    // ── Calculated Values ──────────────────────────────────────────────────────

    const myTotal = useMemo(() => {
        if (!tab || !effectiveUserId) return 0;
        return calculateUserTotal(tab, effectiveUserId);
    }, [tab, effectiveUserId]);

    const billTotal = useMemo(() => {
        if (!tab) return 0;
        return calculateBillTotal(tab);
    }, [tab]);

    const resolveItemSharers = useCallback((claimedBy: string[]) => {
        const registered = memberProfiles.filter(p => claimedBy.includes(p.uid));
        const manual = (tab?.manualMembers ?? []).filter(m => claimedBy.includes(m.id));
        return { registered, manual };
    }, [memberProfiles, tab?.manualMembers]);

    // ── Guest Actions ──────────────────────────────────────────────────────────

    // ...

    const handleJoinAsGuest = async (name: string) => {
        if (!tab) return;
        if (user) {
            showToast("You are logged in. Please use regular join.", "error");
            return;
        }

        const currentCount = tab.members.length + (tab.manualMembers?.length ?? 0);
        if (currentCount >= MAX_TAB_MEMBERS) {
            showToast(`Tab is full (Max ${MAX_TAB_MEMBERS} members)`, "error");
            return;
        }

        const newGuestId = `manual_${Math.random().toString(36).substring(2, 9)}`;
        const newGuest: ManualMember = {
            id: newGuestId,
            name: name.trim(),
            addedAt: new Date().toISOString()
        };

        try {
            await updateTab(tab.id, {
                manualMembers: [...(tab.manualMembers ?? []), newGuest]
            });

            // Save identity
            localStorage.setItem(`tab_guest_identity_${tab.id}`, newGuestId);
            setLocalGuestId(newGuestId);
            showToast("Joined as guest!", "success");
        } catch {
            showToast("Failed to join tab", "error");
        }
    };

    const handleClaimGuest = (manualMemberId: string) => {
        if (!tab) return;
        if (user) {
            void handleClaimMemberMerge(manualMemberId);
            return;
        }

        // Just adopt the ID locally
        localStorage.setItem(`tab_guest_identity_${tab.id}`, manualMemberId);
        setLocalGuestId(manualMemberId);
        showToast("Session recovered!", "success");
    };

    const handleClaimMemberMerge = async (targetId: string) => {
        // Logic for Authenticated User merging a Manual Member into their account
        if (!tab || !user) return;

        try {
            const updates: Partial<Tab> = {};
            // Remove manual member
            updates.manualMembers = (tab.manualMembers ?? []).filter(m => m.id !== targetId);
            // Add current user if not already
            if (!tab.members.includes(user.uid)) {
                updates.members = [...tab.members, user.uid];
            }
            // Update items
            updates.items = tab.items.map(item => ({
                ...item,
                claimedBy: item.claimedBy.map(uid => uid === targetId ? user.uid : uid)
            }));

            await updateTab(tab.id, updates);

            showToast("Identity merged!", "success");
        } catch {
            showToast("Failed to merge identity", "error");
        }
    };

    // Legacy/Auth Join (if they Login efficiently)
    const handleJoinAsMember = async () => {
        if (!tab || !user) return;
        try {
            await updateTab(tab.id, { members: [...tab.members, user.uid] });
            showToast("Joined tab!", "success");
        } catch {
            showToast("Failed to join", "error");
        }
    };

    // ...

    const handleAddItem = async (name: string, price: number, quantity = 1, category: 'Food' | 'Drink' | 'Other' = 'Food') => {
        if (!tab) return;
        if (!effectiveUserId) {
            showToast("You must join the tab first", "error");
            return;
        }

        if (tab.items.length >= MAX_TAB_ITEMS) {
            showToast(`Tab limit reached (Max ${MAX_TAB_ITEMS} items)`, "error");
            return;
        }

        const newItem: TabItem = {
            id: Math.random().toString(36).substring(2, 9),
            name,
            price: price * quantity, // Store total price (Input is per-item)
            quantity,
            category,
            claimedBy: []
        };
        try {
            await updateTab(tab.id, { items: [...tab.items, newItem] });
            setIsAddItemOpen(false);
            showToast("Item added", "success");
        } catch (error) {
            console.error("Error adding item:", error);
            showToast("Failed to add item", "error");
        }
    };

    const handleBulkAddItems = async (result: ScannedReceiptResult) => {
        if (!tab) return;
        
        const scannedItems = result.items;

        if (tab.items.length + scannedItems.length > MAX_TAB_ITEMS) {
            showToast(`Cannot add items. Total would exceed limit of ${MAX_TAB_ITEMS}`, "error");
            return;
        }

        const newItems: TabItem[] = scannedItems.map((item) => ({
            id: Math.random().toString(36).substring(2, 9),
            name: item.name,
            price: item.price * (item.quantity || 1),
            quantity: item.quantity || 1,
            category: item.category,
            claimedBy: [],
        }));

        if (result.tax > 0) {
            newItems.push({
                id: Math.random().toString(36).substring(2, 9),
                name: 'Tax',
                price: result.tax,
                quantity: 1,
                category: 'Other',
                claimedBy: []
            });
        }
        
        const updates: Partial<Tab> = {
            items: [...tab.items, ...newItems],
        };

        if (result.serviceCharge > 0) {
            const subtotal = scannedItems.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
            if (subtotal > 0) {
                const percentage = Math.round((result.serviceCharge / subtotal) * 100);
                if (percentage > 0) {
                    updates.serviceChargePercentage = percentage;
                }
            }
        }

        if (result.discounts.length > 0) {
            const mappedDiscounts = result.discounts.map(d => ({
                id: Math.random().toString(36).substring(2, 9),
                name: d.name || 'Discount',
                type: 'fixed' as const,
                value: d.amount
            }));
            updates.discounts = [...(tab.discounts ?? []), ...mappedDiscounts];
        }

        try {
            await updateTab(tab.id, updates);
            setIsScannerOpen(false);
            showToast(`${newItems.length} items added`, "success");
        } catch {
            showToast("Failed add items", "error");
        }
    };

    // ...

    const updateItemClaim = async (itemId: string, action: 'toggle' | 'increment' | 'decrement' | 'set', value?: number) => {
        if (!tab || !effectiveUserId) {
            showToast("You must join the tab first", "error");
            return;
        }

        const updatedItems = tab.items.map(item => {
            if (item.id === itemId) {
                const qty = item.quantity ?? 1;
                const currentClaims = item.claimedBy;

                if (qty === 1) {
                    // Shared Mode (Single Item) - Toggle
                    const isClaimed = currentClaims.includes(effectiveUserId);
                    return {
                        ...item,
                        claimedBy: isClaimed
                            ? currentClaims.filter(uid => uid !== effectiveUserId)
                            : [...currentClaims, effectiveUserId]
                    };
                } else {
                    // Itemized Mode (Quantity > 1)
                    if (action === 'increment') {
                        if (currentClaims.length < qty) {
                            return { ...item, claimedBy: [...currentClaims, effectiveUserId] };
                        }
                    } else if (action === 'decrement') {
                        const index = currentClaims.indexOf(effectiveUserId);
                        if (index > -1) {
                            const newClaims = [...currentClaims];
                            newClaims.splice(index, 1);
                            return { ...item, claimedBy: newClaims };
                        }
                    } else if (action === 'set' && typeof value === 'number') {
                        const otherClaims = currentClaims.filter(uid => uid !== effectiveUserId);
                        const maxMyClaims = qty - otherClaims.length;
                        const validValue = Math.min(value, maxMyClaims);

                        const myNewClaims = Array(Math.max(0, validValue)).fill(effectiveUserId) as string[];
                        return { ...item, claimedBy: [...otherClaims, ...myNewClaims] };
                    }
                    return item;
                }
            }
            return item;
        });

        try {
            await updateTab(tab.id, { items: updatedItems });
        } catch {
            showToast("Failed to update claim", "error");
        }
    };

    const handleAssignItem = async (itemId: string, newClaimedBy: string[]) => {
        if (!tab || !effectiveUserId) {
            showToast("You must join the tab first", "error");
            return;
        }

        const updatedItems = tab.items.map(item => {
            if (item.id === itemId) {
                return { ...item, claimedBy: newClaimedBy };
            }
            return item;
        });

        try {
            await updateTab(tab.id, { items: updatedItems });
            showToast("Item assigned", "success");
        } catch {
            showToast("Failed to assign item", "error");
        }
    };

    const handleAddManualUser = async (name: string) => {
        if (!tab || !name.trim()) return;

        const currentCount = tab.members.length + (tab.manualMembers?.length ?? 0);
        if (currentCount >= MAX_TAB_MEMBERS) {
            showToast(`Tab is full (Max ${MAX_TAB_MEMBERS} members)`, "error");
            return;
        }

        const newManualUser: ManualMember = {
            id: `manual_${Math.random().toString(36).substring(2, 9)}`,
            name: name.trim(),
            addedAt: new Date().toISOString()
        };
        try {
            await updateTab(tab.id, {
                manualMembers: [...(tab.manualMembers ?? []), newManualUser]
            });
            setIsAddManualUserOpen(false);
            showToast("Friend added", "success");
        } catch {
            showToast("Failed to add friend", "error");
        }
    };

    // Other handlers (Settings, Delete, etc.) - Only for Owner/Auth?
    // Delete Tab -> Owner Only.
    // Update Settings -> Owner Only.

    const updateTabSettings = async (updates: Partial<Tab>) => {
        if (!tab) return;
        // Security: In a real app, Firestore Rules would block this if !isOwner
        await updateTab(tab.id, updates);
        setIsSettingsOpen(false);
        showToast("Settings updated", "success");
    };

    const handleDeleteTab = async () => {
        if (!tab) return;
        await deleteTab(tab.id);
        void navigate('/tabs');
    };

    const handleLeaveTab = async () => {
        if (!tab || !effectiveUserId) return;
        
        const updates: Partial<Tab> = {};

        const newMembers = tab.members.filter(u => u !== effectiveUserId);
        if (newMembers.length !== tab.members.length) {
            updates.members = newMembers;
        }

        const newManual = (tab.manualMembers ?? []).filter(m => m.id !== effectiveUserId);
        if (newManual.length !== (tab.manualMembers?.length ?? 0)) {
            updates.manualMembers = newManual;
        }

        const newItems = tab.items.map(i => ({ ...i, claimedBy: i.claimedBy.filter(u => u !== effectiveUserId) }));
        updates.items = newItems;

        await updateTab(tab.id, updates);
        void navigate('/tabs');
    };

    const handleTransferOwnership = async (newOwnerId: string) => {
        if (!tab || !isOwner) return;
        // Can only transfer to Authenticated Members (UIDs)? 
        // A Guest cannot own a tab.
        // Check if newOwnerId is a UID.
        if (newOwnerId.startsWith('manual_')) {
            showToast("Cannot transfer ownership to a guest", "error");
            return;
        }
        await updateTab(tab.id, { ownerId: newOwnerId });
        setIsTransferConfirmOpen(false);
        setIsTransferOwnershipOpen(false);
        setIsSettingsOpen(false);
        showToast("Ownership transferred", "success");
    };

    // ... [Other handlers removed for brevity, assuming standard implementation]
    // Re-implementing simplified delete handlers
    const handleDeleteItemClick = (id: string) => { setItemToDelete(id); setIsDeleteItemDialogOpen(true); };
    const handleDeleteItemConfirm = async () => {
        if (!tab || !itemToDelete) return;
        await updateTab(tab.id, { items: tab.items.filter(i => i.id !== itemToDelete) });
        setIsDeleteItemDialogOpen(false);
    };
    const handleRemoveMemberClick = (id: string) => { setMemberToRemove(id); setIsRemoveMemberDialogOpen(true); };
    const handleRemoveMemberConfirm = async () => {
        if (!tab || !memberToRemove) return;
        // Logic to remove member (Auth or Manual)
        const newMembers = tab.members.filter(u => u !== memberToRemove);
        const newManual = (tab.manualMembers ?? []).filter(m => m.id !== memberToRemove);
        const newItems = tab.items.map(i => ({ ...i, claimedBy: i.claimedBy.filter(u => u !== memberToRemove) }));
        await updateTab(tab.id, { members: newMembers, manualMembers: newManual, items: newItems });
        setIsRemoveMemberDialogOpen(false);
    };
    const handleRemoveManualUserClick = (id: string) => { setManualUserToRemove(id); setIsRemoveManualUserDialogOpen(true); };
    const handleRemoveManualUserConfirm = async () => {
        if (!tab || !manualUserToRemove) return;
        const newManual = (tab.manualMembers ?? []).filter(m => m.id !== manualUserToRemove);
        const newItems = tab.items.map(i => ({ ...i, claimedBy: i.claimedBy.filter(u => u !== manualUserToRemove) }));
        await updateTab(tab.id, { manualMembers: newManual, items: newItems });
        setIsRemoveManualUserDialogOpen(false);
    };

    const toggleMemberPaymentStatus = async (memberId: string) => {
        if (!tab) return;

        const currentCompleted = tab.completedMembers ?? [];
        const isCompleted = currentCompleted.includes(memberId);

        let newCompleted;
        if (isCompleted) {
            newCompleted = currentCompleted.filter(id => id !== memberId);
        } else {
            newCompleted = [...currentCompleted, memberId];
        }

        try {
            await updateTab(tab.id, { completedMembers: newCompleted });
            showToast(isCompleted ? "Marked as unpaid" : "Marked as paid", "success");
        } catch {
            showToast("Failed to update status", "error");
        }
    };

    const handleEditItem = async (
        itemId: string,
        updates: { name: string; price: number; quantity: number; category: 'Food' | 'Drink' | 'Other' }
    ) => {
        if (!tab) return;
        const updatedItems = tab.items.map(item => {
            if (item.id !== itemId) return item;
            const oldQty = item.quantity ?? 1;
            const newQty = updates.quantity;
            // Preserve claims: trim if new quantity is smaller
            const trimmedClaims = newQty < oldQty ? item.claimedBy.slice(0, newQty) : item.claimedBy;
            return {
                ...item,
                name: updates.name,
                price: updates.price,
                quantity: newQty,
                category: updates.category,
                claimedBy: trimmedClaims,
            };
        });
        try {
            await updateTab(tab.id, { items: updatedItems });
            showToast("Item updated", "success");
        } catch {
            showToast("Failed to update item", "error");
        }
    };


    return {
        // Data
        tab,
        user,
        memberProfiles,
        loading,
        effectiveUserId,
        isOwner,
        isMember,
        myTotal,
        billTotal,
        resolveItemSharers,

        // Modal States
        isSettingsOpen, setIsSettingsOpen,
        isAddItemOpen, setIsAddItemOpen,
        isScannerOpen, setIsScannerOpen,
        isMembersModalOpen, setIsMembersModalOpen,
        isItemAssignmentModalOpen, setIsItemAssignmentModalOpen,
        viewingItem, setViewingItem,
        isRemoveMemberDialogOpen, setIsRemoveMemberDialogOpen,
        isDeleteItemDialogOpen, setIsDeleteItemDialogOpen,
        isDeleteTabDialogOpen, setIsDeleteTabDialogOpen,
        isAddManualUserOpen, setIsAddManualUserOpen,
        isRemoveManualUserDialogOpen, setIsRemoveManualUserDialogOpen,
        isTransferOwnershipOpen, setIsTransferOwnershipOpen,
        isTransferConfirmOpen, setIsTransferConfirmOpen,
        isClaimQuantityOpen, setIsClaimQuantityOpen,
        claimQuantityItem, setClaimQuantityItem,
        memberToRemove, setMemberToRemove,
        itemToDelete, setItemToDelete,
        manualUserToRemove, setManualUserToRemove,

        // Actions
        handleJoinAsGuest,
        handleClaimGuest,
        handleJoinAsMember,
        handleAddItem,
        handleBulkAddItems,
        updateItemClaim,
        handleAssignItem,
        updateTabSettings,
        handleDeleteTab,
        handleLeaveTab,
        handleTransferOwnership,
        handleDeleteItemClick,
        handleDeleteItemConfirm,
        handleRemoveMemberClick,
        handleRemoveMemberConfirm,
        handleAddManualUser,
        handleRemoveManualUserClick,
        handleRemoveManualUserConfirm,
        toggleMemberPaymentStatus,
        handleEditItem,
    };
};
